import { useEffect, useState } from 'react'

/**
 * Keeps a panel in the tree long enough to animate its way out (BRAI-681).
 *
 * A panel rendered straight from `open ? <Panel /> : null` cannot be animated
 * shut: the moment the flag turns the element is already gone, and there is
 * nothing left to transition. This holds it for the length of the transition
 * instead, with `shown` turned off first so the CSS has somewhere to go.
 *
 * Opening is the mirror of that problem. Mounting already-open gives the
 * browser no starting point to animate from, so the first frame is mounted
 * and closed and `shown` arrives on the next one.
 *
 * @param open Whether the panel is wanted.
 * @param exitMs How long the closing transition takes; must match the duration
 *   the panel's own CSS uses, or it will vanish mid-slide or linger after it.
 * @returns `present` — render it at all; `shown` — apply its open styles.
 */
export function usePanelPresence(
  open: boolean,
  exitMs: number
): { present: boolean; shown: boolean } {
  const [present, setPresent] = useState(open)
  const [shown, setShown] = useState(open)

  useEffect(() => {
    if (open) {
      setPresent(true)
      // Two frames, not one. A `requestAnimationFrame` callback runs before
      // the next paint, so turning `shown` on inside the first one applies the
      // open styles in the very paint that mounts the panel — the browser
      // never renders the closed state, so there is nothing to transition
      // from and the panel simply appears. The second frame is the one that
      // guarantees the closed state has actually been painted.
      let settle = 0
      const paint = window.requestAnimationFrame(() => {
        settle = window.requestAnimationFrame(() => setShown(true))
      })
      return () => {
        window.cancelAnimationFrame(paint)
        window.cancelAnimationFrame(settle)
      }
    }

    setShown(false)
    const timer = window.setTimeout(() => setPresent(false), exitMs)
    return () => window.clearTimeout(timer)
  }, [open, exitMs])

  return { present, shown }
}
